export enum LogDevMessageKinds {
  analytics = 'ANALYTICS',
  api = 'API',
  retry = 'RETRY',
  apiError = 'API_ERROR',
  error = 'ERROR',
  ws = 'WS',
  online = 'ONLINE',
  offline = 'OFFLINE',
  info = 'INFO',
}

type MessageOrGetter<
  Message,
  Arguments extends unknown | undefined = undefined,
  Result = any,
> = ((message: Message, ...args: Arguments[]) => Result) | string;

const COLOR_BY_MESSAGE_KIND: Record<LogDevMessageKinds, `#${string}`> = {
  [LogDevMessageKinds.analytics]: '#03a9f4',
  [LogDevMessageKinds.api]: '#fc5fd5',
  [LogDevMessageKinds.retry]: '#ff9500',
  [LogDevMessageKinds.apiError]: '#ff3b30',
  [LogDevMessageKinds.error]: '#d10000',
  [LogDevMessageKinds.ws]: '#2cc153',
  [LogDevMessageKinds.online]: '#59b368',
  [LogDevMessageKinds.offline]: '#db4861',
  [LogDevMessageKinds.info]: '#d6d984',
};

const makeFormatEventMessage =
  (kind: LogDevMessageKinds) =>
  <Content>(...content: Content[]) => {
    const headerCss = [
      `color: ${COLOR_BY_MESSAGE_KIND[kind]}`, // LogDevMessageKinds
      ...content.map(() => 'color: inherit;'),
    ];

    const parts = [`%c${kind}`, ...content.map((item) => `%c${item}`)];

    return [parts.join(' '), ...headerCss];
  };

const normalizeToValue = <Value, Args extends unknown[]>(
  funcOrValue: ((...args: Args) => Value) | Value | undefined,
  ...args: Args
): Value extends undefined ? undefined : Value => {
  if (funcOrValue === undefined) {
    return undefined as Value extends undefined ? undefined : Value;
  }

  return (
    typeof funcOrValue === 'function'
      ? (funcOrValue as (...args: Args) => Value)(...args)
      : funcOrValue
  ) as Value extends undefined ? undefined : Value;
};

export const makeLogDevMessage = <
  Message,
  Arguments extends unknown | undefined = undefined,
>(
  kind: LogDevMessageKinds,
  header: MessageOrGetter<Message, Arguments, string>,
  ...content: [string, MessageOrGetter<Message, Arguments, any>][]
) => {
  const formatEventMessage = makeFormatEventMessage(kind);

  return (message: Message, ...args: Arguments[]) => {
    if (!import.meta.env.DEV && localStorage.getItem('LOGGER') !== 'true') return;

    const messageGroups = formatEventMessage(normalizeToValue(header, message, ...args));

    console.groupCollapsed(...messageGroups);

    content.forEach(([key, item]) => {
      console.log(key, normalizeToValue(item, message, ...args));
    });

    console.groupEnd();
  };
};
